import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useSearchSpotlight } from './SearchSpotlightContext';
import { SearchSpotlightResults } from './SearchSpotlightResults';
import { SPOTLIGHT_Z_INDEX } from './searchSpotlightHelpers';
import './searchSpotlight.css';

export function SearchSpotlightOverlay() {
  const { isOpen, close } = useSearchSpotlight();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsKeyHandlerRef = useRef<((event: ReactKeyboardEvent) => void) | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <Dialog
      open={isOpen}
      onClose={close}
      initialFocus={inputRef}
      style={{ position: 'relative', zIndex: SPOTLIGHT_Z_INDEX }}
    >
      <DialogBackdrop className="search-spotlight-scrim" style={{ zIndex: SPOTLIGHT_Z_INDEX }} />
      <div className="search-spotlight-positioner" style={{ zIndex: SPOTLIGHT_Z_INDEX }}>
        <DialogPanel
          className="search-spotlight-panel"
          data-search-spotlight="true"
          data-testid="search-spotlight"
          onKeyDown={event => {
            resultsKeyHandlerRef.current?.(event);
          }}
        >
          <DialogTitle className="search-spotlight-sr-only">Search DISGENET</DialogTitle>
          <div className="search-spotlight-input-wrap">
            <MagnifyingGlassIcon className="search-spotlight-input-icon" aria-hidden />
            <input
              ref={inputRef}
              type="text"
              autoComplete="off"
              autoFocus
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search genes, diseases, variants, chemicals…"
              aria-label="Search genes, diseases, variants and chemicals"
              data-testid="search-spotlight-input"
              className="search-spotlight-input"
            />
          </div>
          {isOpen && (
            <SearchSpotlightResults query={query} onNavigated={close} onKeyDownRef={resultsKeyHandlerRef} />
          )}
        </DialogPanel>
      </div>
    </Dialog>,
    document.body,
  );
}
